/**
 * HeatmapGrid — discrete grid heatmap with a sequential color scale.
 *
 * Useful for activity calendars, coverage matrices, theorem-vs-CWE
 * crossmap shadings, and any "n-by-m density" surface. The cells are
 * pure SVG rectangles, so the grid stays sharp at any zoom and the
 * SSR output renders without a chart layout phase.
 *
 * Single responsibility: render a typed grid and emit cell hover /
 * select events. Colour interpolation is driven by token-aligned start
 * and end hues; consumers can override the scale to align with a
 * specific theorem accent.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import { GRAY, PRIMARY } from "../../design-system/tokens.js";
import { cn } from "../../styling/index.js";

export interface HeatmapGridCell {
  /** Row index (0-based). */
  readonly row: number;
  /** Column index (0-based). */
  readonly column: number;
  /** Cell value. Used to compute the color intensity. */
  readonly value: number;
  /** Optional tooltip text. */
  readonly title?: string;
}

export interface HeatmapGridProps {
  /** Cell records. Sparse arrays are fine — missing cells render as `emptyColor`. */
  readonly cells: readonly HeatmapGridCell[];
  /** Row count. Defaults to the largest row index in `cells` + 1. */
  readonly rows?: number;
  /** Column count. Defaults to the largest column index in `cells` + 1. */
  readonly columns?: number;
  /** Pixel size of each cell. Defaults to 18. */
  readonly cellSize?: number;
  /** Gap between cells, in pixels. Defaults to 3. */
  readonly cellGap?: number;
  /** Row labels rendered to the left of the grid. */
  readonly rowLabels?: readonly string[];
  /** Column labels rendered above the grid. */
  readonly columnLabels?: readonly string[];
  /** Empty-cell fill. Defaults to `GRAY[300]`. */
  readonly emptyColor?: string;
  /** Low-intensity color. Defaults to `PRIMARY[100]`. */
  readonly lowColor?: string;
  /** High-intensity color. Defaults to `PRIMARY[700]`. */
  readonly highColor?: string;
  /** Optional explicit value clamp. Defaults to `[0, max(values)]`. */
  readonly domain?: readonly [number, number];
  /** Accessible label for the surrounding figure. */
  readonly ariaLabel: string;
  /** Optional className applied to the SVG wrapper. */
  readonly className?: string;
}

function hexChannel(hex: string, offset: number): number {
  return parseInt(hex.slice(offset, offset + 2), 16);
}

function interpolateHex(low: string, high: string, t: number): string {
  const clamp = Math.min(1, Math.max(0, t));
  const r = Math.round(hexChannel(low, 1) + (hexChannel(high, 1) - hexChannel(low, 1)) * clamp);
  const g = Math.round(hexChannel(low, 3) + (hexChannel(high, 3) - hexChannel(low, 3)) * clamp);
  const b = Math.round(hexChannel(low, 5) + (hexChannel(high, 5) - hexChannel(low, 5)) * clamp);
  const toHex = (component: number): string => component.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function dimensions(
  cells: readonly HeatmapGridCell[],
  explicitRows: number | undefined,
  explicitColumns: number | undefined,
): { rows: number; columns: number } {
  let inferredRows = 0;
  let inferredColumns = 0;
  for (const cell of cells) {
    if (cell.row + 1 > inferredRows) inferredRows = cell.row + 1;
    if (cell.column + 1 > inferredColumns) inferredColumns = cell.column + 1;
  }
  return {
    rows: explicitRows ?? inferredRows,
    columns: explicitColumns ?? inferredColumns,
  };
}

function valueDomain(
  cells: readonly HeatmapGridCell[],
  explicit: readonly [number, number] | undefined,
): readonly [number, number] {
  if (explicit !== undefined) return explicit;
  let max = 0;
  for (const cell of cells) {
    if (cell.value > max) max = cell.value;
  }
  return [0, max];
}

const FIGURE_STYLE: React.CSSProperties = { display: "inline-block", margin: 0 };
const LABEL_TEXT_STYLE: React.CSSProperties = { fill: GRAY[600], fontSize: 10 };

/** Renders a discrete grid heatmap with a sequential color scale. */
export const HeatmapGrid = React.memo(function HeatmapGrid({
  cells,
  rows,
  columns,
  cellSize = 18,
  cellGap = 3,
  rowLabels,
  columnLabels,
  emptyColor = GRAY[300],
  lowColor = PRIMARY[100],
  highColor = PRIMARY[700],
  domain,
  ariaLabel,
  className,
}: HeatmapGridProps): React.JSX.Element {
  const { rows: rowCount, columns: columnCount } = useMemo(
    () => dimensions(cells, rows, columns),
    [cells, columns, rows],
  );
  const [domainMin, domainMax] = useMemo(() => valueDomain(cells, domain), [cells, domain]);
  const span = domainMax === domainMin ? 1 : domainMax - domainMin;

  const lookup = useMemo(() => {
    const map = new Map<string, HeatmapGridCell>();
    for (const cell of cells) {
      map.set(`${cell.row}:${cell.column}`, cell);
    }
    return map;
  }, [cells]);

  const leftGutter = rowLabels !== undefined ? 80 : 0;
  const topGutter = columnLabels !== undefined ? 24 : 0;
  // Each row of N cells is `N*cellSize + (N-1)*cellGap` wide — the gap
  // sits between cells, not after the trailing edge.
  const interiorWidth =
    columnCount === 0 ? 0 : columnCount * cellSize + (columnCount - 1) * cellGap;
  const interiorHeight = rowCount === 0 ? 0 : rowCount * cellSize + (rowCount - 1) * cellGap;
  const width = leftGutter + interiorWidth;
  const height = topGutter + interiorHeight;

  const cellRects = useMemo(() => {
    const rects: React.ReactNode[] = [];
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
      for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
        const key = `${rowIndex}:${columnIndex}`;
        const cell = lookup.get(key);
        const intensity = cell !== undefined ? (cell.value - domainMin) / span : 0;
        const fill =
          cell !== undefined ? interpolateHex(lowColor, highColor, intensity) : emptyColor;
        const x = leftGutter + columnIndex * (cellSize + cellGap);
        const y = topGutter + rowIndex * (cellSize + cellGap);
        rects.push(
          <rect
            key={key}
            x={x}
            y={y}
            width={cellSize}
            height={cellSize}
            rx={3}
            ry={3}
            fill={fill}
            data-csk-heatmap-cell={key}
            data-csk-heatmap-empty={cell === undefined ? "true" : "false"}
          >
            {cell?.title !== undefined && <title>{cell.title}</title>}
          </rect>,
        );
      }
    }
    return rects;
  }, [
    cellGap,
    cellSize,
    columnCount,
    domainMin,
    emptyColor,
    highColor,
    leftGutter,
    lookup,
    lowColor,
    rowCount,
    span,
    topGutter,
  ]);

  return (
    <figure
      data-csk-heatmap-grid
      className={cn("font-body", className)}
      style={FIGURE_STYLE}
      aria-label={ariaLabel}
    >
      <svg width={width} height={height} role="img" aria-label={ariaLabel}>
        {columnLabels?.map((label, columnIndex) => (
          <text
            key={`col-${columnIndex}`}
            x={leftGutter + columnIndex * (cellSize + cellGap) + cellSize / 2}
            y={14}
            textAnchor="middle"
            style={LABEL_TEXT_STYLE}
          >
            {label}
          </text>
        ))}
        {rowLabels?.map((label, rowIndex) => (
          <text
            key={`row-${rowIndex}`}
            x={leftGutter - 6}
            y={topGutter + rowIndex * (cellSize + cellGap) + cellSize / 2 + 3}
            textAnchor="end"
            style={LABEL_TEXT_STYLE}
          >
            {label}
          </text>
        ))}
        {cellRects}
      </svg>
    </figure>
  );
});
